"use client";

import { useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Container, Paper, Typography } from "@mui/material";
import { AxiosError } from "axios";
import Header from "@/app/components/HomepageContent/Header";
import Footer from "@/app/components/HomepageContent/Footer";
import {
  fetchGlobalLegalSettingByKey,
  GlobalLegalSettingKey,
} from "@/lib/globalLegalSettingApi";
import { containsHtmlMarkup, sanitizeRichHtml } from "@/lib/legalContent";

interface LegalPageProps {
  pageTitle: string;
  subtitle: string;
  legalKey: GlobalLegalSettingKey;
}

function getErrorMessage(error: unknown) {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data as
      | { message?: unknown; error?: unknown }
      | undefined;

    if (typeof responseData?.message === "string" && responseData.message.trim()) {
      return responseData.message;
    }

    if (typeof responseData?.error === "string" && responseData.error.trim()) {
      return responseData.error;
    }

    if (typeof error.message === "string" && error.message.trim()) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "Unable to load legal content right now. Please try again shortly.";
}

export default function LegalPage({
  pageTitle,
  subtitle,
  legalKey,
}: LegalPageProps) {
  const [content, setContent] = useState("");
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = `CareAscendium - ${pageTitle}`;
  }, [pageTitle]);

  useEffect(() => {
    let isMounted = true;

    const loadLegalContent = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await fetchGlobalLegalSettingByKey(legalKey);

        if (!isMounted) {
          return;
        }

        setContent(response.content || "");
        setLastUpdated(response.modifiedDatetime);
      } catch (unknownError) {
        if (!isMounted) {
          return;
        }

        setLastUpdated(null);
        setError(getErrorMessage(unknownError));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadLegalContent();

    return () => {
      isMounted = false;
    };
  }, [legalKey]);

  const normalizedContent = useMemo(
    () => content.replace(/\u00a0/g, " ").replace(/\r\n/g, "\n").trim(),
    [content],
  );

  const renderedHtml = useMemo(
    () =>
      containsHtmlMarkup(normalizedContent)
        ? sanitizeRichHtml(normalizedContent)
        : "",
    [normalizedContent],
  );

  const contentMode = renderedHtml ? "html" : normalizedContent ? "text" : "empty";

  const plainTextBlocks = useMemo(
    () =>
      normalizedContent
        .split(/\n+/)
        .map((block) => block.trim())
        .filter(Boolean),
    [normalizedContent],
  );

  const formattedLastUpdated = useMemo(() => {
    if (!lastUpdated) {
      return "";
    }

    const parsedDate = new Date(lastUpdated);
    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(parsedDate);
  }, [lastUpdated]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fafafa",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      <Box
        sx={{
          background: "linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)",
          py: 8,
          px: 4,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage:
              "radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)",
            backgroundSize: "50px 50px",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h1"
            sx={{
              color: "white",
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            {pageTitle}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "rgba(255,255,255,0.9)",
              maxWidth: "900px",
              mx: "auto",
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: "1.2rem", md: "1.5rem" },
            }}
          >
            {subtitle}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            bgcolor: "white",
            borderRadius: 3,
            border: "1px solid rgba(21,60,127,0.1)",
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#153C7F" }} />
            </Box>
          ) : null}

          {!isLoading && error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {!isLoading && !error && contentMode === "empty" ? (
            <Alert severity="info">
              This legal content has not been published yet in Global Legal Setting.
            </Alert>
          ) : null}

          {!isLoading && !error && contentMode === "html" ? (
            <Box
              sx={{
                color: "#333",
                lineHeight: 1.9,
                fontSize: "1rem",
                "& h1, & h2, & h3, & h4, & h5, & h6": {
                  color: "#153C7F",
                  fontWeight: 700,
                  mt: 4,
                  mb: 2,
                },
                "& h2": {
                  color: "#153C7F",
                  fontWeight: 700,
                  fontSize: "1.5rem",
                  lineHeight: 1.3,
                  mb: 3,
                },
                "& p": {
                  mb: 2,
                },
                "& ul, & ol": {
                  pl: 3,
                  mb: 2,
                },
                "& li": {
                  mb: 1,
                },
                "& a": {
                  color: "#153C7F",
                  textDecoration: "underline",
                },
                "& table": {
                  width: "100%",
                  borderCollapse: "collapse",
                  mb: 2,
                },
                "& th, & td": {
                  border: "1px solid rgba(21,60,127,0.2)",
                  p: 1,
                },
              }}
              dangerouslySetInnerHTML={{ __html: renderedHtml }}
            />
          ) : null}

          {!isLoading && !error && contentMode === "text" ? (
            <Box
              sx={{
                color: "#333",
                lineHeight: 1.9,
                fontSize: "1rem",
                wordBreak: "break-word",
              }}
            >
              {plainTextBlocks.map((block, index) => (
                <Typography
                  key={`${legalKey}-block-${index}`}
                  component="p"
                  sx={{
                    mb: index === plainTextBlocks.length - 1 ? 0 : 2.5,
                    color: "inherit",
                    fontSize: "inherit",
                    lineHeight: "inherit",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {block}
                </Typography>
              ))}
            </Box>
          ) : null}

          {!isLoading &&
          !error &&
          contentMode !== "empty" &&
          formattedLastUpdated ? (
            <Box
              sx={{
                mt: 6,
                pt: 3,
                borderTop: "1px solid rgba(21,60,127,0.1)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  fontSize: "0.95rem",
                  fontStyle: "italic",
                }}
              >
                Last Updated: {formattedLastUpdated}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
}
